/**
 * Native Features - Fonctionnalités natives iOS/Android
 * Améliore l'expérience utilisateur avec des feedback haptiques,
 * animations natives, et intégrations système
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

export const IS_NATIVE = Capacitor.isNativePlatform();
export const IS_IOS = Capacitor.getPlatform() === 'ios';
export const IS_ANDROID = Capacitor.getPlatform() === 'android';

// ============================================
// HAPTIC FEEDBACK
// ============================================

/**
 * Vibration légère pour les interactions (boutons, toggles)
 */
export const hapticLight = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Vibration moyenne pour les actions importantes (validation, navigation)
 */
export const hapticMedium = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Vibration forte pour les actions majeures (soumission, succès)
 */
export const hapticHeavy = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.impact({ style: ImpactStyle.Heavy });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Notification de succès (triple vibration)
 */
export const hapticSuccess = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.notification({ type: NotificationType.Success });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Notification d'erreur
 */
export const hapticError = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.notification({ type: NotificationType.Error });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Notification d'avertissement
 */
export const hapticWarning = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.notification({ type: NotificationType.Warning });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Vibration spéciale pour collecte de trésor (pattern festif)
 */
export const hapticTreasureCollect = async () => {
  if (!IS_NATIVE) return;
  try {
    // Séquence de vibrations festives
    await Haptics.impact({ style: ImpactStyle.Heavy });
    await new Promise(r => setTimeout(r, 100));
    await Haptics.impact({ style: ImpactStyle.Medium });
    await new Promise(r => setTimeout(r, 100));
    await Haptics.impact({ style: ImpactStyle.Light });
    await new Promise(r => setTimeout(r, 100));
    await Haptics.notification({ type: NotificationType.Success });
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

/**
 * Jouer un son de collecte (pièces)
 * Utilise Web Audio API pour une meilleure compatibilité
 */
export const playTreasureSound = () => {
  try {
    // Créer un contexte audio
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    
    const ctx = new AudioContext();
    
    // Séquence de "dings" comme des pièces
    const playNote = (frequency, startTime, duration, gain = 0.3) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(gain, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = ctx.currentTime;
    
    // Mélodie de pièces qui tombent (arpège ascendant)
    playNote(523.25, now, 0.15, 0.3);        // C5
    playNote(659.25, now + 0.08, 0.15, 0.25);  // E5
    playNote(783.99, now + 0.16, 0.15, 0.25);  // G5
    playNote(1046.50, now + 0.24, 0.2, 0.3);   // C6
    playNote(1318.51, now + 0.35, 0.3, 0.2);   // E6 (note finale)
    
    // Fermer le contexte après la fin des sons
    setTimeout(() => ctx.close(), 1000);
  } catch (e) {
    console.warn('Sound not available:', e);
  }
};

/**
 * Feedback complet pour collecte de trésor (son + vibration)
 */
export const treasureCollectFeedback = async (isRare = false) => {
  // Jouer le son
  playTreasureSound();
  
  // Vibration
  if (IS_NATIVE) {
    if (isRare) {
      // Vibration plus intense pour les trésors rares
      await hapticTreasureCollect();
    } else {
      await hapticSuccess();
    }
  }
};

/**
 * Vibration pour sélection (menus, listes)
 */
export const hapticSelection = async () => {
  if (!IS_NATIVE) return;
  try {
    await Haptics.selectionStart();
    await Haptics.selectionChanged();
    await Haptics.selectionEnd();
  } catch (e) {
    console.warn('Haptics not available:', e);
  }
};

// ============================================
// OFFLINE MODE
// ============================================

/**
 * Vérifier si l'appareil est en ligne
 */
export const isOnline = () => {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
    return navigator.onLine;
  }
  return true;
};

/**
 * Écouter les changements de connectivité
 */
export const onConnectivityChange = (callback) => {
  if (typeof window === 'undefined') return () => {};
  
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// ============================================
// ANIMATIONS NATIVES
// ============================================

/**
 * Déclencher une animation de rebond sur un élément
 */
export const animateBounce = (element) => {
  if (!element) return;
  element.style.transform = 'scale(0.95)';
  element.style.transition = 'transform 0.1s ease-out';
  setTimeout(() => {
    element.style.transform = 'scale(1.05)';
    setTimeout(() => {
      element.style.transform = 'scale(1)';
    }, 100);
  }, 100);
};

/**
 * Déclencher une animation de shake (erreur)
 */
export const animateShake = (element) => {
  if (!element) return;
  const originalTransform = element.style.transform || '';
  element.style.transition = 'transform 0.1s ease-in-out';
  
  const shake = async () => {
    for (let i = 0; i < 3; i++) {
      element.style.transform = 'translateX(-5px)';
      await new Promise(r => setTimeout(r, 50));
      element.style.transform = 'translateX(5px)';
      await new Promise(r => setTimeout(r, 50));
    }
    element.style.transform = originalTransform;
  };
  
  shake();
};

/**
 * Animation de succès (pulse vert)
 */
export const animateSuccess = (element) => {
  if (!element) return;
  element.style.boxShadow = '0 0 0 0 rgba(34, 197, 94, 0.7)';
  element.style.transition = 'box-shadow 0.3s ease-out';
  
  setTimeout(() => {
    element.style.boxShadow = '0 0 0 10px rgba(34, 197, 94, 0)';
    setTimeout(() => {
      element.style.boxShadow = '';
    }, 300);
  }, 10);
};

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook combiné pour feedback tactile + animation
 * @param {string} type - 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning'
 */
export const triggerFeedback = async (type = 'light', element = null) => {
  switch (type) {
    case 'light':
      await hapticLight();
      if (element) animateBounce(element);
      break;
    case 'medium':
      await hapticMedium();
      if (element) animateBounce(element);
      break;
    case 'heavy':
      await hapticHeavy();
      if (element) animateBounce(element);
      break;
    case 'success':
      await hapticSuccess();
      if (element) animateSuccess(element);
      break;
    case 'error':
      await hapticError();
      if (element) animateShake(element);
      break;
    case 'warning':
      await hapticWarning();
      if (element) animateShake(element);
      break;
    default:
      await hapticLight();
  }
};

// ============================================
// SHARE FUNCTIONALITY (NATIVE)
// ============================================

import { Share } from '@capacitor/share';

/**
 * Partager du contenu via l'interface native
 */
export const nativeShare = async ({ title, text, url, dialogTitle }) => {
  if (!IS_NATIVE) {
    // Fallback pour le web
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message };
      }
    }
    return { success: false, error: 'Share not supported' };
  }
  
  try {
    const result = await Share.share({
      title: title || 'CareWash GO',
      text: text || '',
      url: url || '',
      dialogTitle: dialogTitle || 'Partager'
    });
    return { success: true, result };
  } catch (e) {
    console.warn('Share error:', e);
    return { success: false, error: e.message };
  }
};

// ============================================
// STATUS BAR (iOS)
// ============================================

import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Configurer la barre de statut iOS/Android
 * Note: Sur Android 15+, le style est géré par le système avec edge-to-edge
 */
export const configureStatusBar = async (style = 'dark') => {
  if (!IS_NATIVE) return;
  
  try {
    // setStyle fonctionne sur toutes les versions Android et iOS
    // Il définit la couleur des icônes (clair ou foncé)
    if (style === 'dark') {
      await StatusBar.setStyle({ style: Style.Dark });
    } else {
      await StatusBar.setStyle({ style: Style.Light });
    }
  } catch (e) {
    console.warn('StatusBar config error:', e);
  }
};

/**
 * Définir la couleur de fond de la barre de statut (Android < 15 uniquement)
 * DEPRECATED sur Android 15+ (SDK 35) avec edge-to-edge
 * Cette fonction est maintenant un NO-OP sur Android pour éviter les warnings Google Play
 */
export const setStatusBarColor = async (color) => {
  // Désactivé sur Android pour éviter l'utilisation des APIs dépréciées
  // Sur Android 15+, le edge-to-edge gère automatiquement l'apparence
  // Sur les versions antérieures, la couleur est définie dans le thème natif
  if (IS_ANDROID) {
    console.log('StatusBar.setBackgroundColor disabled for Android 15+ compliance');
    return;
  }
  
  // Sur iOS, pas d'API setBackgroundColor
};


// ============================================
// SPLASH SCREEN
// ============================================

import { SplashScreen } from '@capacitor/splash-screen';

/**
 * Cacher le splash screen manuellement
 */
export const hideSplashScreen = async () => {
  if (!IS_NATIVE) return;
  
  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    console.warn('SplashScreen hide error:', e);
  }
};

/**
 * Montrer le splash screen
 */
export const showSplashScreen = async () => {
  if (!IS_NATIVE) return;
  
  try {
    await SplashScreen.show({
      autoHide: false,
      fadeInDuration: 300,
      fadeOutDuration: 300
    });
  } catch (e) {
    console.warn('SplashScreen show error:', e);
  }
};

// ============================================
// EXPORT ALL
// ============================================

export default {
  // Platform detection
  IS_NATIVE,
  IS_IOS,
  IS_ANDROID,
  
  // Haptics
  hapticLight,
  hapticMedium,
  hapticHeavy,
  hapticSuccess,
  hapticError,
  hapticWarning,
  hapticSelection,
  
  // Offline
  isOnline,
  onConnectivityChange,
  
  // Animations
  animateBounce,
  animateShake,
  animateSuccess,
  triggerFeedback,
  
  // Share
  nativeShare,
  
  // Status Bar
  configureStatusBar,
  setStatusBarColor,
  
  // Splash Screen
  hideSplashScreen,
  showSplashScreen
};
